import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Alert,
  Badge,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import {
  format,
  parseISO,
  isSameDay,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { useStepperContext } from "../../../../Stepper/StepperContext";

const TripSelectionStep = ({
  selectedWorkOrder,
  onSelectTrip,
  currentCraneInspection,
}) => {
  const { updateStepData } = useStepperContext();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(null);
  const [tripForDate, setTripForDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [datesToTrips, setDatesToTrips] = useState({});
  const [loading, setLoading] = useState(false);

  // Reset selections when work order changes
  useEffect(() => {
    setSelectedDate(null);
    setSelectedTechnician(null);
    setCalendarMonth(null);
    setTripForDate(null);
    setAvailableDates([]);
    setDatesToTrips({});
  }, [selectedWorkOrder]);

  // Check if we're moving within the same work order
  const isSameWorkOrder = useMemo(() => {
    if (!selectedWorkOrder || !currentCraneInspection) return false;
    return selectedWorkOrder.id === currentCraneInspection.workOrderId;
  }, [selectedWorkOrder, currentCraneInspection]);

  // Calculate available dates and set initial calendar month
  useEffect(() => {
    if (!selectedWorkOrder || !selectedWorkOrder.trips) {
      return;
    }

    setLoading(true);

    try {
      const trips = selectedWorkOrder.trips || [];

      if (trips.length === 0) {
        return;
      }

      // Find all available dates across all trips
      const allDates = [];
      const dateToTripMap = {};

      trips.forEach((trip) => {
        const start = parseISO(trip.startDate);
        const end = parseISO(trip.endDate);

        const currentDate = new Date(start);
        while (currentDate <= end) {
          const dateString = currentDate.toISOString().split("T")[0];

          // If it's the same work order, exclude the current inspection date
          if (isSameWorkOrder && currentCraneInspection?.date) {
            const currentInspectionDate = new Date(currentCraneInspection.date)
              .toISOString()
              .split("T")[0];
            if (dateString === currentInspectionDate) {
              currentDate.setDate(currentDate.getDate() + 1);
              continue;
            }
          }

          // Store the date and associate it with this trip
          if (!dateToTripMap[dateString]) {
            const newDate = new Date(currentDate);
            allDates.push(newDate);
            dateToTripMap[dateString] = trip;
          }

          currentDate.setDate(currentDate.getDate() + 1);
        }
      });

      setAvailableDates(allDates);
      setDatesToTrips(dateToTripMap);

      // Set initial calendar month if not set yet
      if (!calendarMonth && trips.length > 0) {
        // Find the earliest trip start date to set as default month
        const earliestDate = trips.reduce((earliest, trip) => {
          const startDate = parseISO(trip.startDate);
          return earliest === null || startDate < earliest
            ? startDate
            : earliest;
        }, null);

        if (earliestDate) {
          setCalendarMonth(earliestDate);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [
    selectedWorkOrder,
    calendarMonth,
    isSameWorkOrder,
    currentCraneInspection,
  ]);

  // Handle date selection - define before any conditional returns
  const handleDateSelect = useCallback(
    (date) => {
      if (!date) return;

      // Check if the selected date is available
      const isDateAvailable = availableDates.some((availableDate) =>
        isSameDay(availableDate, date)
      );

      if (isDateAvailable) {
        setSelectedDate(date);
        setSelectedTechnician(null);

        // Find which trip this date belongs to
        const dateString = date.toISOString().split("T")[0];
        const trip = datesToTrips[dateString];
        setTripForDate(trip);

        const selection = {
          ...trip,
          selectedDate: date,
          selectedTechnician: null,
        };

        if (onSelectTrip) {
          onSelectTrip(selection);
        }
      }
    },
    [availableDates, datesToTrips, onSelectTrip]
  );

  // Handle technician selection - define before any conditional returns
  const handleTechnicianSelect = useCallback(
    (technician) => {
      setSelectedTechnician(technician);

      // Update data in stepper context and parent component
      const selection = {
        ...tripForDate,
        selectedDate: selectedDate,
        selectedTechnician: technician,
      };

      updateStepData("selectedTrip", selection);
      if (onSelectTrip) {
        onSelectTrip(selection);
      }
    },
    [tripForDate, selectedDate, onSelectTrip, updateStepData]
  );

  // Get calendar navigation boundaries - memoized to prevent recalculation
  // Define before any conditional returns
  const calendarBoundaries = useMemo(() => {
    const trips = selectedWorkOrder?.trips || [];
    if (!trips || trips.length === 0) return {};

    // Find earliest and latest dates from all trips
    const { earliest, latest } = trips.reduce(
      (acc, trip) => {
        const start = parseISO(trip.startDate);
        const end = parseISO(trip.endDate);

        return {
          earliest:
            !acc.earliest || start < acc.earliest ? start : acc.earliest,
          latest: !acc.latest || end > acc.latest ? end : acc.latest,
        };
      },
      { earliest: null, latest: null }
    );

    return {
      fromDate: earliest ? startOfMonth(earliest) : null,
      toDate: latest ? endOfMonth(latest) : null,
    };
  }, [selectedWorkOrder]);

  // Memoize date info for the header - define before any conditional returns
  const dateRangeInfo = useMemo(() => {
    const trips = selectedWorkOrder?.trips || [];
    if (!trips || trips.length === 0) return null;

    const sortedTrips = [...trips].sort(
      (a, b) => new Date(a.startDate) - new Date(b.startDate)
    );

    return {
      start: format(parseISO(sortedTrips[0].startDate), "MMM d, yyyy"),
      end: format(
        parseISO(sortedTrips[sortedTrips.length - 1].endDate),
        "MMM d, yyyy"
      ),
    };
  }, [selectedWorkOrder]);

  // Memoize the disabled days function - define before any conditional returns
  const isDayDisabled = useCallback(
    (day) => {
      return !availableDates.some((date) => isSameDay(date, day));
    },
    [availableDates]
  );

  // Conditional rendering after all hooks are defined
  if (!selectedWorkOrder) {
    return (
      <Alert color="danger" className="p-3 m-3">
        Please select a work order first
      </Alert>
    );
  }

  const trips = selectedWorkOrder.trips || [];

  if (trips.length === 0) {
    return (
      <Alert color="warning" className="p-3 m-3">
        No trips available for this work order
      </Alert>
    );
  }

  // Check if no dates are available after filtering
  if (availableDates.length === 0 && !loading) {
    return (
      <Alert color="warning" className="p-3 m-3">
        {isSameWorkOrder
          ? "No alternative dates available for this work order. The inspection is already scheduled for the only available date in this work order."
          : "No available dates found for this work order."}
      </Alert>
    );
  }

  return (
    <div className="p-4">
      <h5 className="mb-4">Select Date and Technician</h5>

      <Row className="mb-4">
        <Col md={7}>
          <Card>
            <CardHeader className="bg-light">
              <h6 className="mb-0">Select an Available Date</h6>
              {dateRangeInfo && (
                <div className="small text-muted">
                  Available dates: {dateRangeInfo.start} - {dateRangeInfo.end}
                  {isSameWorkOrder && (
                    <div className="text-warning">
                      <small>
                        Current date excluded:{" "}
                        {currentCraneInspection?.date
                          ? format(
                              parseISO(currentCraneInspection.date),
                              "MMM d, yyyy"
                            )
                          : "N/A"}
                      </small>
                    </div>
                  )}
                </div>
              )}
            </CardHeader>
            <CardBody className="text-center">
              {/* <style dangerouslySetInnerHTML={{ __html: dayPickerStyles }} /> */}

              {loading ? (
                <div className="py-4">
                  <Spinner color="primary" size="sm" />
                  <p className="text-muted mt-2">Loading available dates...</p>
                </div>
              ) : availableDates.length > 0 ? (
                <DayPicker
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  defaultMonth={calendarMonth}
                  fromDate={calendarBoundaries.fromDate}
                  toDate={calendarBoundaries.toDate}
                  disabled={isDayDisabled}
                  modifiers={{
                    available: availableDates,
                  }}
                  className="mx-auto border rounded p-2"
                />
              ) : (
                <p className="text-muted my-4">No available dates found</p>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col md={5}>
          {selectedDate &&
          tripForDate &&
          tripForDate.technicians &&
          tripForDate.technicians.length > 0 ? (
            <Card>
              <CardHeader className="bg-light">
                <h6 className="mb-0">Select a Technician</h6>
                <div className="small text-muted">
                  For {format(selectedDate, "MMMM d, yyyy")}
                </div>
              </CardHeader>
              <CardBody className="p-0">
                <ul className="list-group list-group-flush">
                  {tripForDate.technicians.map((tech) => (
                    <li
                      key={tech.id}
                      className={`list-group-item list-group-item-action ${
                        selectedTechnician?.id === tech.id ? "active" : ""
                      }`}
                      onClick={() => handleTechnicianSelect(tech)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{tech.name}</strong>
                          {tech.email && (
                            <div
                              className={`small ${
                                selectedTechnician?.id === tech.id
                                  ? "text-white-50"
                                  : "text-muted"
                              }`}
                            >
                              {tech.email}
                            </div>
                          )}
                        </div>
                        {tech.isLead && (
                          <Badge
                            color={
                              selectedTechnician?.id === tech.id
                                ? "light"
                                : "success"
                            }
                            style={{
                              color:
                                selectedTechnician?.id === tech.id
                                  ? "black"
                                  : "white",
                            }}
                          >
                            Lead
                          </Badge>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : selectedDate ? (
            <Alert color="info" className="mt-3">
              No technicians available for this date
            </Alert>
          ) : (
            <Alert color="secondary" className="p-1">
              Please select a date to see available technicians
            </Alert>
          )}
        </Col>
      </Row>

      {selectedDate && tripForDate && selectedTechnician && (
        <Card className="mt-4 border-success">
          <CardHeader className="bg-success">
            <h6 className="mb-0 text-white">Selection Summary</h6>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md={4}>
                <h6>Trip Details</h6>
                <p className="mb-1">
                  <strong>Trip #:</strong> {tripForDate.tripNumber}
                </p>
                <p>
                  <strong>Period:</strong>{" "}
                  {format(parseISO(tripForDate.startDate), "MMM d, yyyy")} -{" "}
                  {format(parseISO(tripForDate.endDate), "MMM d, yyyy")}
                </p>
              </Col>
              <Col md={4}>
                <h6>Selected Date</h6>
                <p>{format(selectedDate, "MMMM d, yyyy")}</p>
              </Col>
              <Col md={4}>
                <h6>Technician</h6>
                <p className="mb-1">
                  <strong>{selectedTechnician.name}</strong>
                </p>
                {selectedTechnician.email && (
                  <p className="mb-0 text-muted small">
                    {selectedTechnician.email}
                  </p>
                )}
                {selectedTechnician.isLead && (
                  <Badge color="success">Lead</Badge>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default TripSelectionStep;
